define('lq/events/eventing',[
    'underscore',
    'lq/events/actions/eventAction',
    'lq/events/actions/login',
    'lq/events/actions/thirdPartyLogin',
    'lq/events/actions/federatedLogin',
    'lq/events/processEventActionsUrl',
    'lq/events/abortError',
    'when'
], function (_, EventAction, Login, ThirdPartyLogin, FederatedLogin, processEventActionsUrl, AbortError, when) {
    'use strict';

    var getFullEventName = function (event) {
        return event.eventSourceDropId
            ? (event.eventSourceDropId + '.' + event.eventName)
            : event.eventName;
    };

    var Eventing = function (events, piping, eventActionFactory) {
        this.events = events;
        this.piping = piping;
        this.eventActionFactory = eventActionFactory;
        this.subcriptions = {};
        this.interceptors = [];
    };

    Eventing.prototype.subscribe = function(eventName, callback) {
        if (!this.subcriptions[eventName])
            this.subcriptions[eventName] = [];
        this.subcriptions[eventName].push(callback);
    };

    Eventing.prototype.addIntercept = function (callback) {
        this.interceptors.push(callback);
    };

    Eventing.prototype.removeIntercept = function (callback) {
        var index = this.interceptors.indexOf(callback);
        if (index >= 0)
            this.interceptors.splice(index, 1);
    };

    Eventing.prototype.hasSubscribers = function (eventName) {
        return this.subcriptions[eventName] !== undefined && this.subcriptions[eventName].length > 0;
    };

    Eventing.prototype.publish = function (eventName, $trigger, args, callback) {
        _.each(this.subcriptions[eventName] || [], function (subscription) {
            subscription.apply(subscription, [$trigger, args, callback]);
        });
        
        // notify parent about triggered event, but don't send it to just any domain
        if (window.parent) {
            var eventObj = {
                eventName: eventName,
                args: [args] // staying backwards compatible with existing custom code
            };

            var targetOrigin = this.piping.embeddable
                ? '*'
                : window.location.protocol + '//' + window.location.host;
            window.parent.postMessage(JSON.stringify(eventObj), targetOrigin);
        }

        if (this.interceptors.length > 0) {
            var interceptedEventObj = {
                eventName: eventName,
                args: [args]
            };

            _.each(this.interceptors, function (interceptor) {
                interceptor(interceptedEventObj);
            });
        }
    };

    Eventing.prototype.bindEvents = function () {
        var self = this;

        if (!self.events || self.events.length === 0) {
            return;
        }
        var eventGroups = _.groupBy(self.events, function (event) {
            return getFullEventName(event);
        });
        _.each(eventGroups, function (eventGroup) {
            self.bindEventGroup(eventGroup, self.piping);
        });

        for (var i = 0; i < 20; i++) {
            endLogGroup();
        }

        self.triggerPageLoadEvent();
    };

    Eventing.prototype.triggerPageLoadEvent = function () {
        var self = this;

        var afterPageLoad = function () {
            ThirdPartyLogin.afterPageLoad(self);
            FederatedLogin.afterPageLoad(self);
        };

        var hasPageLoadEventHandler = _.any(self.events, function (event) { return getFullEventName(event) === 'PageLoad' });
        if (!hasPageLoadEventHandler) {
            afterPageLoad();
        }

        this.publish('PageLoad', null, null, function () {
            afterPageLoad();
        });
    };

    Eventing.prototype.beforePageRender = function() {
        Login.beforePageRender(this);
    };

    Eventing.prototype.bindEventGroup = function (eventGroup, piping) {
        if (eventGroup.length === 0)
            return;

        var conditionEvaluators = _.map(eventGroup, function (event) {
            return getConditionEvaluator(event, piping);
        });

        var self = this;
        this.subscribe(
            getFullEventName(eventGroup[0]),
            function (eventSource, eventArg, eventCompleted) {
                piping.busyIndicator.show();

                _.each(conditionEvaluators, function (conditionEvaluator) {
                    if (!conditionEvaluator) return;
                    conditionEvaluator.clearResult();
                });

                var hasEventsWithConditions = !!_.find(eventGroup, function (event) {
                    var currentConditionEvaluator = getConditionEvaluator(event, piping);
                    return !!currentConditionEvaluator;
                });

                var selectEventsTasks = _.map(eventGroup, function (event) {
                    return function (arrayOfEvents) {
                        var currentConditionEvaluator = getConditionEvaluator(event, piping);

                        if (!currentConditionEvaluator) {
                            arrayOfEvents.push(event);
                            return arrayOfEvents;
                        }

                        return currentConditionEvaluator.evaluate()
                            .then(function (shouldExecute) {
                                if (event.invertCondition)
                                    shouldExecute = !shouldExecute;

                                if (shouldExecute)
                                    arrayOfEvents.push(event);

                                return arrayOfEvents;
                            });
                    };
                });

                var afterChainCompleted = function () {
                    piping.busyIndicator.hide();

                    if (typeof eventCompleted === 'function')
                        eventCompleted();
                };

                if (hasEventsWithConditions) {
                    when.pipeline(selectEventsTasks, []).done(function (arrayOfEvents) {
                        var executeNextEvent = function () {
                            if (arrayOfEvents.length === 0) {
                                afterChainCompleted();
                            } else {
                                var event = arrayOfEvents.splice(0, 1)[0];
                                createAndExecuteEventActions(eventSource, event, eventArg, executeNextEvent, piping, self);
                            }
                        };

                        executeNextEvent();
                    });
                } else {
                    var arrayOfEvents = [];
                    _.each(selectEventsTasks, function (eventTask) {
                        eventTask(arrayOfEvents);
                    });

                    var executeNextEvent = function () {
                        if (arrayOfEvents.length === 0) {
                            afterChainCompleted();
                        } else {
                            var event = arrayOfEvents.splice(0, 1)[0];
                            createAndExecuteEventActions(eventSource, event, eventArg, executeNextEvent, piping, self);
                        }
                    };

                    executeNextEvent();
                }
            });
    };

    var getConditionEvaluator = function (event, piping) {
        if (event.conditionId)
            return piping.conditions.getConditionEvaluator(event.conditionId);
        return null;
    };

    var createAndExecuteEventActions = function (eventSource, event, eventArg, eventCompleted, piping, eventing) {
        if (event.conditionId) {
            var conditionEvaluator = getConditionEvaluator(event, piping);
            var inverse = "";
            if (event.invertCondition) {
                inverse = "NOT ";
            }
            startLogGroup("EVENT: " + event.eventName + " (" + inverse + conditionEvaluator.conditionName + ")");
        } else {
            startLogGroup("EVENT: " + event.eventName);
        }

        var eventActions = eventing.eventActionFactory.createEventActions(event, eventSource, eventArg);
        executeEventActionBatches(event, createEventActionBatches(eventActions), eventCompleted, piping, eventing);
    };

    var createEventActionBatches = function (eventActions) {
        if (eventActions.length == 0) return [];

        var batches = [];
        var currentBatch = [];
        var currentBatchType;
        batches.push(currentBatch);

        _.forEach(eventActions, function (eventAction) {
            var batchType = eventAction.getBatchType();
            if ((currentBatchType !== undefined && batchType !== currentBatchType) || batchType === EventAction.DO_NOT_BATCH) {
                if (currentBatch.length > 0) { //no need to start a new batch when the current batch is empty
                    currentBatch = [];
                    batches.push(currentBatch);
                }
            }
            currentBatch.push(eventAction);
            currentBatchType = batchType;
        });

        return batches;
    };

    var executeEventActionBatches = function (event, eventActionBatches, eventCompleted, piping, eventing) {
        if (eventActionBatches.length === 0) { //all batches have been executed, event execution is finished completely
            endLogGroup();
            if (typeof eventCompleted === "function") {
                eventCompleted();
            }
            return;
        }

        var batch = eventActionBatches[0];
        eventActionBatches.splice(0, 1); //removes first element from array

        startLogGroup("<BATCH>");

        var executeNextBatch = function () {
            endLogGroup();
            executeEventActionBatches(event, eventActionBatches, eventCompleted, piping, eventing);
        };

        createEventActionRequests(piping, batch, function (eventActionRequests) {
            if (eventActionRequests.length === 0) {
                executeBatch(batch, executeNextBatch, []);
            } else {
                var parameters = piping.parameters;

                piping.dispatcher.dispatch(processEventActionsUrl.get(piping, 'Events'),
                    {
                        actionId: piping.actionId,
                        globalParameters: parameters.globals.getAll(),
                        localParameters: parameters.locals.getAll(),
                        requests: eventActionRequests
                    },
                    function (response) {
                        if (response.abort) {
                            piping.busyIndicator.forceHide();

                            if (response.eventName)
                                piping.eventing.publish(response.eventName);
                            else
                                alert(response.reason);
                        } else {
                            _.each(response.globalParameters, function (val, key) {
                                parameters.globals.setValue(key, val);
                            });

                            _.each(response.localParameters, function (val, key) {
                                parameters.locals.setValue(key, val);
                            });

                            executeBatch(batch, executeNextBatch, response.responses);
                        }
                    }
                );
            }
        });
    };

    var createEventActionRequests = function (piping, batch, continuation, eventActionRequests) {
        eventActionRequests = eventActionRequests || [];

        if (batch.length === 0) { //all requests have been created, continue with executing batch
            continuation(eventActionRequests);
            return;
        }

        var eventAction = batch[0];

        batch = batch.slice(0); //copies array (we cannot "clear" actual batch queue yet, because we still need to execute it)
        batch.splice(0, 1); //removes first element from array

        var processEventActionRequest = function (request) {
            if (self.stopExecuting) {
                self.stopExecuting = false;
                piping.busyIndicator.forceHide();
                return;
            }
            if (request != undefined) {
                request.eventActionId = eventAction.eventActionId;
                eventActionRequests.push(request);
            }
            createEventActionRequests(piping, batch, continuation, eventActionRequests);
        };

        try {
            var eventActionRequest = eventAction.createEventActionRequest();
        }
        catch (err) {
            if (err instanceof AbortError) {
                self.stopExecuting = true;
            } else {
                throw err;
            }
        }

        if (typeof eventActionRequest == "function") {
            eventActionRequest(function (request) {
                processEventActionRequest(request);
            });
        } else {
            processEventActionRequest(eventActionRequest);
        }
    };

    var executeBatch = function (batch, continuation, responses) {
        if (batch.length === 0) { //batch is fully executed, move on to next batch
            continuation();
            return;
        }

        var eventAction = batch[0];
        batch.splice(0, 1); //removes first element from array

        var eventActionResponse = _.find(responses, function (response) {
            return response.EventActionId === eventAction.eventActionId;
        });

        startLogGroup(eventAction.eventActionId + " - " + eventAction.eventActionTypeName);
        log("Executing '" + eventAction.eventActionId + " - " + eventAction.eventActionTypeName + "'");
        eventAction.execute(
            function () {
                log("Finished executing '" + eventAction.eventActionId + " - " + eventAction.eventActionTypeName + "'");
                endLogGroup();
                executeBatch(batch, continuation, responses);
            }, //continue callback
            eventActionResponse
        );
    };

    // **************************************************************
    // maybe we should disable log statements by default, because apparently it has some performance impact, 
    // it should only be done when validating the page or something like that or with a special argument, or when debugging tools are open, or ...?
    // **************************************************************

    var logIndentationLevel = 0;
    var log = function (message) {
        if (window.console && window.console.log) {
            var logPrefix = '';
            if (typeof console.group === "undefined") {
                for (var i = 0; i < logIndentationLevel; i++) {
                    logPrefix += "\t";
                }
            }
            console.log(logPrefix + message);
        }
    };

    var startLogGroup = function (groupName) {
        logIndentationLevel++;
        if (typeof console !== "undefined" && typeof console.group !== "undefined") {
            console.group(groupName);
        } else {
            log(groupName);
        }
    };

    var endLogGroup = function () {
        logIndentationLevel--;
        if (typeof console !== "undefined" && typeof console.groupEnd !== "undefined") {
            console.groupEnd();
        }
    };

    return Eventing;
});
